import config from "../../../config/config";
import { authFetch, buildAuthHeaders } from "../../../public/api/httpClient";

const STUDENTS_BASE = `${config.BASE_URL}/student-registrations`;

async function parseJson(res) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { message: text || "Invalid response" };
  }
}

export const getStudentStats = async () => {
  const res = await authFetch(`${STUDENTS_BASE}/stats`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(data.message || "Failed to fetch student stats");
  }
  return data;
};

export const getStudents = async () => {
  const res = await authFetch(`${STUDENTS_BASE}/`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(data.message || "Failed to fetch student stats");
  }
  return data;
};

export const getSportsList = async () => {
  const res = await authFetch(`${STUDENTS_BASE}/sports`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(data.message || "Failed to fetch sports list");
  }
  return data;
};

export const createStudent = async (csrfToken, payload) => {
  try {
    const res = await authFetch(`${STUDENTS_BASE}/create`, {
      method: "POST",
      credentials: "include",
      headers: buildAuthHeaders(
        csrfToken ? { "X-CSRF-Token": csrfToken } : {},
      ),
      body: JSON.stringify(payload),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      return {
        data: null,
        error: {
          message: data.message || "Failed to create user",
          details: data.details,
        },
      };
    }
    return { data, error: null };
  } catch (error) {
    return {
      data: null,
      error: {
        message: error.message || "Failed to create user",
      },
    };
  }
};

export const getStudentById = async (id) => {
  const res = await authFetch(
    `${STUDENTS_BASE}/${encodeURIComponent(String(id))}`,
    {
      credentials: "include",
      headers: buildAuthHeaders(),
    },
  );
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(data.message || "Failed to load student");
  }
  return data;
};

export const updateStudent = async (id, payload, csrfToken) => {
  const res = await authFetch(
    `${STUDENTS_BASE}/update/${encodeURIComponent(String(id))}`,
    {
      method: "PUT",
      credentials: "include",
      headers: buildAuthHeaders(
        csrfToken ? { "X-CSRF-Token": csrfToken } : {},
      ),
      body: JSON.stringify(payload),
    },
  );
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(
      data.message ||
        (typeof data.details === "string"
          ? data.details
          : "Failed to update student"),
    );
  }
  return data;
};

export const searchStudents = async ({
  page,
  limit,
  sortColumn,
  sortOrder,
  searchName,
  searchCode,
  q,
  sportId,
  email,
  phone,
  gender,
  address,
  guardianName,
  emergencyContact,
  dob,
  studentCreatedAt,
  studentMemRegDate,
  membershipName,
  codeExact,
  nameExact,
  emailExact,
  phoneExact,
  genderExact,
  addressExact,
  guardianNameExact,
  emergencyContactExact,
  dobExact,
  studentCreatedAtExact,
  studentMemRegDateExact,
  membershipNameExact,
} = {}) => {
  const params = new URLSearchParams();
  params.set("page", page);
  params.set("limit", limit);
  if (sortColumn) {
    params.set("sort_by", sortColumn);
    params.set("order", sortOrder);
  }
  if (q) params.set("q", q);
  if (searchName) params.set("name", searchName);
  if (searchCode) params.set("code", searchCode);
  if (sportId) params.set("sport_id", String(sportId));
  if (email) params.set("email", email);
  if (phone) params.set("phone", phone);
  if (gender) params.set("gender", gender);
  if (address) params.set("address", address);
  if (guardianName) params.set("guardian_name", guardianName);
  if (emergencyContact) params.set("emergency_contact", emergencyContact);
  if (dob) params.set("dob", dob);
  if (studentCreatedAt) params.set("student_created_at", studentCreatedAt);
  if (studentMemRegDate) params.set("student_mem_reg_date", studentMemRegDate);
  if (membershipName) params.set("membership_name", membershipName);
  if (codeExact) params.set("code_exact", codeExact);
  if (nameExact) params.set("name_exact", nameExact);
  if (emailExact) params.set("email_exact", emailExact);
  if (phoneExact) params.set("phone_exact", phoneExact);
  if (genderExact) params.set("gender_exact", genderExact);
  if (addressExact) params.set("address_exact", addressExact);
  if (guardianNameExact) params.set("guardian_name_exact", guardianNameExact);
  if (emergencyContactExact) params.set("emergency_contact_exact", emergencyContactExact);
  if (dobExact) params.set("dob_exact", dobExact);
  if (studentCreatedAtExact) params.set("student_created_at_exact", studentCreatedAtExact);
  if (studentMemRegDateExact) params.set("student_mem_reg_date_exact", studentMemRegDateExact);
  if (membershipNameExact) params.set("membership_name_exact", membershipNameExact);

  const res = await authFetch(`${STUDENTS_BASE}/search?${params.toString()}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(data.message || "Failed to fetch student stats");
  }
  return data;
};

export const getStudentFilterValues = async (field, limit = 300) => {
  const params = new URLSearchParams();
  params.set("field", field);
  params.set("limit", String(limit));
  const res = await authFetch(`${STUDENTS_BASE}/filter-values?${params.toString()}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(data.message || "Failed to load filter values");
  }
  return Array.isArray(data.values) ? data.values : [];
};

export const deactiveStudent = async (id, csrfToken) => {
  const res = await authFetch(
    `${STUDENTS_BASE}/${encodeURIComponent(String(id))}`,
    {
      method: "DELETE",
      credentials: "include",
      headers: buildAuthHeaders(
        csrfToken ? { "X-CSRF-Token": csrfToken } : {},
      ),
    },
  );
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(data.message || "Failed to deactivate student");
  }
  return data;
};
